export const VID = 0x0519;
export const PID = 0x2013;
export const EP_OUT = 3;
export const EP_IN = 0x81;
export const TIMEOUT_MS = 5000;
export const CHUNK_SIZE = 4096;

/**
 * THE HEAD'S RESOLUTION — 203 DPI, which is 203 / 25.4 = 7.99 dots per millimetre.
 *
 * Every dots-to-mm figure the operator reads rests on this, and it was a bare `8` written out nine
 * times across the printer, the panel and the warnings. A magic number repeated is a number that
 * gets changed in eight places.
 *
 * NOT AN ASSUMPTION — the presets below corroborate it. 384 dots is the standard printable width of
 * 58mm stock (48mm, the paper less its unprintable edges) and 576 is the standard for 80mm (72mm).
 * Both only work out at 8 dots/mm. Line spacing of 28 dots = 3.5mm per line agrees as well.
 *
 * A 300 DPI head would make this 11.8, and then these presets would be wrong too — so if a unit
 * ever prints at a different scale, this constant and PAPER_PRESETS move together.
 */
export const DOTS_PER_MM = 8;

/** Dots the paper advances per printed line at the spacing `printTicket` sets (ESC 3 n, n = 28). */
export const LINE_SPACING_DOTS = 28;

/** Dots per character at the base font and 1x size — 384 dots across 32 columns on 58mm stock. */
export const DOTS_PER_CHAR = 12;

/** Dots -> millimetres, for anything an operator reads. */
export const mm = (dots: number): number => dots / DOTS_PER_MM;

/** Millimetres -> dots, for anything an operator types. The head cannot address half a dot. */
export const dots = (millimetres: number): number => Math.round(millimetres * DOTS_PER_MM);

export interface PaperPreset { leftMargin: number; width: number }

export const PAPER_PRESETS: Record<string, PaperPreset> = {
  // 384 dots = 48mm printable; 576 = 72mm. See DOTS_PER_MM.
  '58': { leftMargin: 224, width: 384 },
  '80': { leftMargin: 0, width: 576 },
};

export const BARCODE_NAMES: Record<string, number> = {
  'UPC-A': 0, 'UPC-E': 1, 'EAN13': 2, 'EAN8': 3,
  'CODE39': 4, 'ITF': 5, 'CODABAR': 6, 'STDEAN13': 7,
  'STDEAN8': 8, 'CODE93': 9, 'CODE128': 10,
};

export const STATUS_DESCRIPTIONS: Record<number, string> = {
  0: 'Printer is ready',
  1: 'Printer offline or no power',
  2: 'Printer / library mismatch',
  3: 'Printer head is opened',
  4: 'Cutter not reset',
  5: 'Head temp abnormal',
  6: 'Blackmark error',
  7: 'Paper out',
  8: 'Paper low',
};

export const STATUS5_DESCRIPTIONS: Record<number, string> = {
  4: 'Acceptor error', 5: 'Paper jam', 6: 'Paper stuck',
  7: 'Paper pulled', 8: 'Paper sensor',
};
